import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import "./DailyWordStyles.css";
import {FaRedo} from "react-icons/fa";
import {getWebsiteLanguage, type LanguageKey, useLanguage} from "../../Authentication/LanguageContext";
import {DailyWordItem, getDailyWordsImageUrl, useDailyWordsData} from "./DownloadDailyWords";

type DailyWordsProps = {compact?: boolean};

/** Translation field names match the existing production DailyWords API. */
const TRANSLATION_SUFFIX: Record<LanguageKey, string> = {
  en: "english", ta: "tamil", si: "sinhala", hi: "hindi", ar: "arabic",
  tr: "turkish", ru: "russian", zh: "chinese", fr: "french", es: "spanish", de: "german",
};

const DEFAULT_DAILY_WORD: DailyWordItem = {
  id: 1,
  headergerman: "Wort des Tages", headerenglish: "Daily Word", headertamil: "தினசரி வார்த்தை",
  headersinhala: "දෛනික වචනය", headerhindi: "दैनिक शब्द", headerarabic: "كلمة اليوم",
  headerturkish: "Günün Kelimesi", headerrussian: "Слово дня", headerchinese: "每日单词",
  headerfrench: "Mot du jour", headerspanish: "Palabra del día",
  subheadergerman: "", subheaderenglish: "", subheadertamil: "", subheadersinhala: "",
  subheaderhindi: "", subheaderarabic: "", subheaderturkish: "", subheaderrussian: "",
  subheaderchinese: "", subheaderfrench: "", subheaderspanish: "",
  wordgerman: "Hallo", wordenglish: "Hello", wordtamil: "வணக்கம்", wordsinhala: "ආයුබෝවන්",
  wordhindi: "नमस्ते", wordarabic: "مرحبا", wordturkish: "Merhaba", wordrussian: "Привет",
  wordchinese: "你好", wordfrench: "Bonjour", wordspanish: "Hola",
  sentencegerman: "Hallo, wie geht es dir heute?", sentenceenglish: "Hello, how are you today?",
  sentencetamil: "வணக்கம், இன்று நீங்கள் எப்படி இருக்கிறீர்கள்?",
  sentencesinhala: "ආයුබෝවන්, ඔබ අද කොහොමද?",
  sentencehindi: "नमस्ते, आज आप कैसे हैं?", sentencearabic: "مرحبًا، كيف حالك اليوم؟",
  sentenceturkish: "Merhaba, bugün nasılsın?", sentencerussian: "Привет, как дела сегодня?",
  sentencechinese: "你好，你今天怎么样？", sentencefrench: "Bonjour, comment allez-vous aujourd’hui ?",
  sentencespanish: "¡Hola! ¿Cómo estás hoy?", image: null,
};

const readString = (data: DailyWordItem, name: string): string => {
  const value = data[name];
  return typeof value === "string" ? value.trim() : "";
};
const formatDate = (): string => {
  const date = new Date();
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};

const DailyWords: React.FC<DailyWordsProps> = ({compact = false}) => {
  const {language} = useLanguage();
  const {dailyWord, loading, error, initialized, retryDailyWord, refreshDailyWord} = useDailyWordsData();
  const [playing, setPlaying] = useState<string | null>(null);
  const [isOnline, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine);
  const activeSpeech = useRef<SpeechSynthesisUtterance | null>(null);
  const data = dailyWord ?? DEFAULT_DAILY_WORD;
  const suffix = TRANSLATION_SUFFIX[language];
  const speechLanguage = language === "de" ? "de-DE" : getWebsiteLanguage(language).speech;

  const header = readString(data, "headergerman") || DEFAULT_DAILY_WORD.headergerman;
  const translatedHeader = language === "de" ? "" : readString(data, `header${suffix}`);
  const word = readString(data, "wordgerman") || DEFAULT_DAILY_WORD.wordgerman;
  const translation = language === "de" ? "" : readString(data, `word${suffix}`);
  const sentence = readString(data, "sentencegerman") || DEFAULT_DAILY_WORD.sentencegerman;
  const translatedSentence = language === "de" ? "" : readString(data, `sentence${suffix}`);
  const imageSrc = useMemo(() => {
    const next = getDailyWordsImageUrl(data.image);
    return typeof next === "string" && next.trim() ? next.trim() : undefined;
  }, [data.image]);
  const todayDate = useMemo(formatDate, []);

  useEffect(() => {
    const online = () => setOnline(true);
    const offline = () => setOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      activeSpeech.current = null;
    };
  }, []);

  useEffect(() => {
    // Switching languages should never keep speaking the previous translation.
    if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
    setPlaying(null);
  }, [language]);

  const speak = useCallback((text: string, lang: string, name: string): void => {
    if (!text || typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = lang === "de-DE" ? 0.9 : 0.92;
    utterance.pitch = 1;
    utterance.onend = () => setPlaying(current => current === name ? null : current);
    utterance.onerror = () => setPlaying(current => current === name ? null : current);
    activeSpeech.current = utterance;
    setPlaying(name);
    window.speechSynthesis.speak(utterance);
  }, []);

  const showError = initialized && !loading && !dailyWord && Boolean(error);
  return (
    <section className={`daily-word-card dl-bilingual-daily ${compact ? "daily-word-card-compact" : ""}`}
      aria-label="Wort des Tages">
      <div className="daily-word-header">
        <div className="daily-word-header-left">
          <span className="daily-word-header-title" data-site-no-translate="true">{header}</span>
          {translatedHeader && translatedHeader !== header ? (
            <span className="dl-daily-header-translation dl-native-language" data-site-no-translate="true" lang={language} dir={language === "ar" ? "rtl" : "auto"}>{translatedHeader}</span>
          ) : null}
        </div>
        <time className="daily-word-date-badge" dateTime={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}>{todayDate}</time>
      </div>
      <div className="daily-word-panel">
        <div className="daily-word-panel-base" aria-hidden="true" />
        <div className="daily-word-panel-gloss" aria-hidden="true" />
        {showError ? (
          <div className="daily-word-error" role="status">
            <strong>Daily Words konnten nicht geladen werden.</strong>
            <small>{error}</small>
            <button type="button" onClick={retryDailyWord}><FaRedo /> Erneut versuchen</button>
          </div>
        ) : (
          <>
            {imageSrc && isOnline && !compact ? (
              <img src={imageSrc} alt="" className="daily-word-image" loading="lazy" />
            ) : null}
            <div className="daily-word-content">
              <div className="dl-daily-line-group">
                <button type="button" className={`daily-word-section daily-word-word-section ${playing === "word" ? "is-playing" : ""}`}
                  onClick={() => speak(word, "de-DE", "word")}
                  onDoubleClick={() => void refreshDailyWord()} aria-label={`Deutsch anhören: ${word}`}>
                  <strong className="daily-word-main-word">{word}</strong>
                  
                </button>
                {translation ? (
                  <button type="button" className="dl-daily-translated dl-native-language" lang={language} dir={language === "ar" ? "rtl" : "auto"}
                    onClick={() => speak(translation, speechLanguage, "translated-word")}
                    aria-label={`Übersetzung anhören: ${translation}`}>
                    <span>{translation}</span>
                  </button>
                ) : null}
              </div>
              <div className="dl-daily-line-group dl-daily-sentence-group">
                <button type="button" className={`daily-word-section daily-word-sentence-section ${playing === "sentence" ? "is-playing" : ""}`}
                  onClick={() => speak(sentence, "de-DE", "sentence")}
                  onDoubleClick={() => void refreshDailyWord()} aria-label={`Deutschen Satz anhören: ${sentence}`}>
                  <strong className="daily-word-main-sentence">{sentence}</strong>
                  
                </button>
                {translatedSentence ? (
                  <button type="button" className="dl-daily-translated dl-daily-translated-sentence dl-native-language"
                    lang={language} dir={language === "ar" ? "rtl" : "auto"}
                    onClick={() => speak(translatedSentence, speechLanguage, "translated-sentence")}
                    aria-label={`Übersetzten Satz anhören: ${translatedSentence}`}>
                    <span>{translatedSentence}</span>
                  </button>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
export default DailyWords;
